import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import BarcodeScanner from "../components/BarcodeScanner";
import {
  ClipboardList,
  Loader2,
  Package,
  Plus,
  Trash2,
  CheckCircle,
  Barcode,
  ShoppingCart,
  Calendar,
  Building2,
  AlertTriangle,
  ArrowLeft,
  Send,
  Search,
  X
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const PRIORIDADES = [
  { value: "baja", label: "Baja", color: "bg-slate-100 text-slate-600" },
  { value: "normal", label: "Normal", color: "bg-blue-100 text-blue-600" },
  { value: "alta", label: "Alta", color: "bg-orange-100 text-orange-600" },
  { value: "urgente", label: "Urgente", color: "bg-red-100 text-red-600" }
];

export default function NuevaRequisicionPage() {
  const [articulos, setArticulos] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedArticulo, setSelectedArticulo] = useState(null);
  const [articuloSearch, setArticuloSearch] = useState("");
  const [showArticulosList, setShowArticulosList] = useState(false);
  const [items, setItems] = useState([]);
  const [itemCantidad, setItemCantidad] = useState(1);
  const [itemObservaciones, setItemObservaciones] = useState("");
  const [formData, setFormData] = useState({
    departamento_id: "",
    fecha_requerida: new Date().toISOString().split('T')[0], // Fecha de hoy por defecto
    prioridad: "normal",
    motivo: ""
  });
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [articulosRes, deptRes] = await Promise.all([
        axios.get(`${API}/inventario/articulos?limit=1000`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders())
      ]);
      // Handle paginated response format
      const articulosData = articulosRes.data.items || articulosRes.data;
      setArticulos(Array.isArray(articulosData) ? articulosData.filter(a => a.activo !== false) : []);
      setDepartamentos(deptRes.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBarcodeScanned = async (codigo) => {
    try {
      const response = await axios.get(`${API}/inventario/articulos/codigo/${codigo}`, getAuthHeaders());
      if (response.data) {
        setSelectedArticulo(response.data);
        toast.success(`Artículo encontrado: ${response.data.nombre}`);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Artículo no encontrado con ese código");
      }
    }
  };

  const handleSelectArticulo = (articuloId) => {
    const articulo = articulos.find(a => a.id === articuloId);
    if (articulo) {
      setSelectedArticulo(articulo);
      setArticuloSearch("");
      setShowArticulosList(false);
    }
  };

  // Filter articles based on search
  const filteredArticulos = articulos.filter(art => {
    if (!articuloSearch.trim()) return true;
    const search = articuloSearch.toLowerCase();
    return (
      art.codigo?.toLowerCase().includes(search) ||
      art.nombre?.toLowerCase().includes(search) ||
      art.descripcion?.toLowerCase().includes(search)
    );
  });

  const addToList = () => {
    if (!selectedArticulo) {
      toast.error("Selecciona un artículo");
      return;
    }
    if (itemCantidad <= 0) {
      toast.error("La cantidad debe ser mayor a 0");
      return;
    }

    // Validar stock disponible
    const stockActual = selectedArticulo.stock_actual || 0;
    if (stockActual <= 0) {
      toast.error(`No hay stock disponible de "${selectedArticulo.nombre}". Stock actual: 0`, {
        duration: 5000,
        icon: "⚠️"
      });
      return;
    }

    // Calcular cantidad ya solicitada de este artículo en la lista
    const existingItem = items.find(i => i.articulo_id === selectedArticulo.id);
    const cantidadYaSolicitada = existingItem ? existingItem.cantidad : 0;
    const cantidadTotal = cantidadYaSolicitada + itemCantidad;

    // Validar que la cantidad total no exceda el stock
    if (cantidadTotal > stockActual) {
      toast.error(`Stock insuficiente. Disponible: ${stockActual}, Ya solicitado: ${cantidadYaSolicitada}, Intentando agregar: ${itemCantidad}`, {
        duration: 5000,
        icon: "⚠️"
      });
      return;
    }

    if (existingItem) {
      // Update existing item
      const newItems = [...items];
      const existingIndex = items.findIndex(i => i.articulo_id === selectedArticulo.id);
      newItems[existingIndex].cantidad += itemCantidad;
      if (itemObservaciones) {
        newItems[existingIndex].observaciones = itemObservaciones;
      }
      setItems(newItems);
      toast.success(`Cantidad actualizada: ${newItems[existingIndex].cantidad} unidades`);
    } else {
      // Add new item
      setItems([...items, {
        articulo_id: selectedArticulo.id,
        articulo: selectedArticulo,
        cantidad: itemCantidad,
        observaciones: itemObservaciones
      }]);
      toast.success(`"${selectedArticulo.nombre}" agregado a la lista`);
    }

    // Reset
    setSelectedArticulo(null);
    setItemCantidad(1);
    setItemObservaciones("");
  };

  const removeFromList = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      toast.error("Agrega al menos un artículo");
      return;
    }

    try {
      setSaving(true);
      
      const requestData = {
        ...formData,
        items: items.map(i => ({
          articulo_id: i.articulo_id,
          cantidad: i.cantidad,
          observaciones: i.observaciones || ""
        }))
      };
      
      const response = await axios.post(`${API}/requisiciones`, requestData, getAuthHeaders());
      
      toast.success(`Requisición ${response.data.numero} creada exitosamente`);
      navigate("/requisiciones");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear requisición");
    } finally {
      setSaving(false);
    }
  };

  const totalItems = items.reduce((sum, i) => sum + i.cantidad, 0);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="nueva-requisicion-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/requisiciones")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Nueva Requisición
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Solicitar materiales del inventario
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* General Info */}
            <Card>
              <CardHeader>
                <CardTitle>Información General</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Departamento Solicitante
                    </Label>
                    <Select value={formData.departamento_id} onValueChange={(v) => setFormData({ ...formData, departamento_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar departamento..." />
                      </SelectTrigger>
                      <SelectContent>
                        {departamentos.map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Fecha de Solicitud
                    </Label>
                    <Input
                      type="date"
                      value={formData.fecha_requerida}
                      disabled
                      className="bg-slate-100 cursor-not-allowed"
                    />
                    <p className="text-xs text-slate-500">La fecha se establece automáticamente</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Prioridad</Label>
                    <Select value={formData.prioridad} onValueChange={(v) => setFormData({ ...formData, prioridad: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORIDADES.map(p => (
                          <SelectItem key={p.value} value={p.value}>
                            <Badge className={p.color}>{p.label}</Badge>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Motivo / Justificación</Label>
                    <Textarea
                      value={formData.motivo}
                      onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                      placeholder="Explique brevemente por qué necesita estos materiales..."
                      rows={2}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Barcode Scanner */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Barcode className="w-5 h-5" />
                  Agregar Artículos
                </CardTitle>
                <CardDescription>
                  Escanea el código de barras o selecciona manualmente
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BarcodeScanner onScan={handleBarcodeScanned} />
              </CardContent>
            </Card>

            {/* Article Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Seleccionar Artículo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Artículo</Label>
                    <div className="relative">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          value={selectedArticulo ? `${selectedArticulo.codigo} - ${selectedArticulo.nombre}` : articuloSearch}
                          onChange={(e) => {
                            setArticuloSearch(e.target.value);
                            setSelectedArticulo(null);
                            setShowArticulosList(true);
                          }}
                          onFocus={() => setShowArticulosList(true)}
                          placeholder="Buscar por código o nombre..."
                          className="pl-10 pr-10"
                          data-testid="articulo-search-input"
                        />
                        {(selectedArticulo || articuloSearch) && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedArticulo(null);
                              setArticuloSearch("");
                              setShowArticulosList(false);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Dropdown list */}
                      {showArticulosList && !selectedArticulo && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {filteredArticulos.length === 0 ? (
                            <div className="p-3 text-center text-slate-500">
                              No se encontraron artículos
                            </div>
                          ) : (
                            filteredArticulos.slice(0, 50).map(art => (
                              <button
                                key={art.id}
                                type="button"
                                onClick={() => handleSelectArticulo(art.id)}
                                className="w-full px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 last:border-0"
                              >
                                <span className="font-mono text-xs text-slate-500 bg-slate-100 dark:bg-slate-600 px-2 py-0.5 rounded">{art.codigo}</span>
                                <span className="flex-1 truncate">{art.nombre}</span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    
                    {selectedArticulo && (
                      <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-teal-800">{selectedArticulo.nombre}</p>
                            <p className="text-sm text-teal-600 font-mono">{selectedArticulo.codigo}</p>
                          </div>
                          <Badge className="bg-teal-100 text-teal-700">Seleccionado</Badge>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Cantidad Solicitada *</Label>
                    <Input
                      type="number"
                      value={itemCantidad}
                      onChange={(e) => setItemCantidad(parseFloat(e.target.value) || 0)}
                      min={0.01}
                      step={1}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Observaciones del Artículo</Label>
                    <Input
                      value={itemObservaciones}
                      onChange={(e) => setItemObservaciones(e.target.value)}
                      placeholder="Especificaciones o notas..."
                    />
                  </div>
                </div>
                <Button 
                  onClick={addToList} 
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  disabled={!selectedArticulo || itemCantidad <= 0}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar a la Requisición
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Items List */}
          <div className="space-y-6">
            <Card className="border-teal-200">
              <CardHeader className="bg-teal-50 rounded-t-lg">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Artículos Solicitados
                  </span>
                  <Badge variant="secondary">{items.length} items</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {items.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>Agrega artículos a la requisición</p>
                  </div>
                ) : (
                  <ScrollArea className="h-64">
                    <div className="space-y-2">
                      {items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.articulo?.nombre}</p>
                            <p className="text-xs text-slate-500 font-mono">{item.articulo?.codigo}</p>
                            {item.observaciones && (
                              <p className="text-xs text-slate-400 mt-1">{item.observaciones}</p>
                            )}
                          </div>
                          <div className="text-right mr-2">
                            <Badge className="bg-teal-100 text-teal-700">{item.cantidad} {item.articulo?.unidad_medida}</Badge>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => removeFromList(index)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}

                {items.length > 0 && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Total artículos:</span>
                      <span className="font-bold">{items.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Total unidades:</span>
                      <span className="font-bold">{totalItems}</span>
                    </div>
                    <Button 
                      onClick={handleSubmit} 
                      className="w-full bg-teal-600 hover:bg-teal-700"
                      disabled={saving}
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Enviar Requisición
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
